
import { useState, useEffect, useCallback } from 'react';
import { Table, Column } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';

export interface ActivityLogEntry {
  id: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  action: string;
  resourceType?: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  createdAt: string;
}

interface PaginatedActivityLogs {
  items: ActivityLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const PAGE_SIZE = 20;
const ACTION_OPTIONS = [
  '',
  'auth.login',
  'prayer.approve',
  'prayer.reject',
  'prayer.edit',
  'prayer.respond',
  'queue.add',
  'queue.remove',
  'user.create',
  'user.invite',
  'user.update_role',
  'user.deactivate',
  'settings.update',
  'simulation.start',
  'simulation.stop',
];

function formatTimestamp(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return iso;
  }
}

export function ActivityLogViewer() {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('per_page', String(PAGE_SIZE));
    if (actionFilter) params.set('action', actionFilter);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const response = await api.get<PaginatedActivityLogs>(
      `/admin/activity-logs?${params.toString()}`
    );
    if (response.data) {
      setLogs(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } else {
      setLogs([]);
      setTotal(0);
      setTotalPages(0);
    }
    setIsLoading(false);
  }, [page, actionFilter, fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns: Column<ActivityLogEntry>[] = [
    {
      key: 'createdAt',
      header: 'Time',
      width: '160px',
      render: (row) => (
        <span className="text-slate-600 dark:text-slate-400 text-sm whitespace-nowrap">
          {formatTimestamp(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'user',
      header: 'User',
      width: '180px',
      render: (row) => (
        <div className="flex flex-col">
          {row.userName && (
            <span className="font-medium text-slate-900 dark:text-white">{row.userName}</span>
          )}
          {row.userEmail && (
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{row.userEmail}</span>
          )}
          {!row.userName && !row.userEmail && (
            <span className="text-slate-500 dark:text-slate-400 text-sm">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '140px',
      render: (row) => (
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{row.action}</span>
      ),
    },
    {
      key: 'resource',
      header: 'Resource',
      width: '120px',
      render: (row) => (
        <span className="text-slate-600 dark:text-slate-400 text-sm">
          {row.resourceType && row.resourceId
            ? `${row.resourceType}`
            : row.resourceType || '—'}
        </span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (row) =>
        row.details && Object.keys(row.details).length > 0 ? (
          <pre className="text-xs text-slate-600 dark:text-slate-400 overflow-x-auto max-w-xs truncate" title={JSON.stringify(row.details)}>
            {JSON.stringify(row.details)}
          </pre>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'ipAddress',
      header: 'IP',
      width: '100px',
      render: (row) => (
        <span className="text-slate-500 dark:text-slate-400 text-sm font-mono">
          {row.ipAddress || '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Activity Log</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Audit trail of who did what and when. Admin only.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">Action</span>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-2 py-1.5"
          >
            {ACTION_OPTIONS.map((a) => (
              <option key={a || 'all'} value={a}>
                {a || 'All'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">From</span>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">To</span>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            className="w-40"
          />
        </label>
        <Button variant="outline" size="sm" onClick={fetchLogs}>
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <Table<ActivityLogEntry>
          columns={columns}
          data={logs}
          keyExtractor={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="No activity logs found"
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={total}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
